import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { debounce } from "lodash-es";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { formValueSelector, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { Callout, CalloutIntent, HStack, InfoToggletip } from "widgets";

import { type ICreateProjectParams } from "@/api/WebApiClient.ts";
import { simpleFieldValidation } from "@/app-utils/form.ts";
import { type Capability, type Project, type User, type UserGroup } from "@/graphql.ts";
import { getProjectLabel, replaceProjectTermWithSystemSetting } from "@/redux/modules/config.ts";
import {
  type IProjectCapabilitiesListState,
  loadProjectCapabilitiesList,
} from "@/redux/modules/project-capabilities-list.ts";
import { createNewProject, validateProjectNameIsDefined } from "@/redux/modules/project-list.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { DEFAULT_PROJECT_CAPABILITIES, PUBLIC_USER } from "@/settings/settings.ts";

import { Dialog } from "../core/overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";

interface ICreateProjectDialogOwnProps {
  // show?: boolean;
  onApply?: () => void;
  onCancel?: () => void;
}

interface ICreateProjectDialogAppStateProps {
  currentUser: User;
  isPublicProject: boolean;
  selectedUserGroup: UserGroup;
  selectedUsers: User[];
  appName: string;
  initialValues: Partial<FormData>;
  projectCapabilitiesListState: IProjectCapabilitiesListState;
}

interface ICreateProjectDialogDispatchProps {
  createProject: (payload: ICreateProjectParams) => Promise<Project>;
  loadProjectCapabilitiesList: () => void;
}

export interface ICreateProjectDialogProps
  extends ICreateProjectDialogAppStateProps,
    ICreateProjectDialogDispatchProps,
    ICreateProjectDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(state: IAppState): ICreateProjectDialogAppStateProps {
  const createProjectFormValueSelector = formValueSelector(CreateProjectDialogImpl.PROJECT_DIALOG_FORM_NAME);
  const selectedUsers: User[] =
    createProjectFormValueSelector(state, CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_MEMBERS) || [];
  const selectedUserGroup: UserGroup = createProjectFormValueSelector(
    state,
    CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_USER_GROUP,
  );
  const userGroups: UserGroup[] = state.auth.session && state.auth.session.userGroups;
  return {
    currentUser: state.auth.session,
    // if there is no member or just the 'public' user in userList
    isPublicProject: selectedUsers && !!selectedUsers.find((user: User) => user.id === PUBLIC_USER.id),
    selectedUserGroup: selectedUserGroup,
    selectedUsers: selectedUsers,
    appName: state.config.configuration ? state.config.configuration.application.name : "",
    projectCapabilitiesListState: state.projectCapabilities,
    initialValues: {
      [CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_CAPABILITIES]: state.projectCapabilities.projectCapabilities.filter(
        (c: Capability) => DEFAULT_PROJECT_CAPABILITIES.find((cc) => cc === c.id),
      ),
      [CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_USER_GROUP]: userGroups && userGroups.length && userGroups[0],
    },
  };
}

function mapDispatchToProps(dispatch): ICreateProjectDialogDispatchProps {
  return {
    createProject: (payload: ICreateProjectParams) => dispatch(createNewProject(payload)),
    loadProjectCapabilitiesList: () => dispatch(loadProjectCapabilitiesList()),
  };
}

class CreateProjectDialogImpl extends Component<ICreateProjectDialogProps> {
  private static ASYNC_VALIDATION_DEBOUNCE_TIMER = 300;

  public static PROJECT_DIALOG_FORM_NAME = "createProject";
  public static PROJECT_DIALOG_FIELD_NAME = "projectName";
  public static PROJECT_DIALOG_FIELD_USER_GROUP = "userGroup";
  public static PROJECT_DIALOG_FIELD_MEMBERS = "selectUser";
  public static PROJECT_DIALOG_FIELD_DESCRIPTION = "projectDescription";
  public static PROJECT_DIALOG_FIELD_SENDEMAILS = "sendInviteEmails";
  public static PROJECT_DIALOG_FIELD_CAPABILITIES = "projectCapabilities";

  constructor(props: ICreateProjectDialogProps) {
    super(props);
  }

  componentDidMount() {
    this.debouncedProjectNameValidation = debounce(
      this.debouncedProjectNameValidation,
      CreateProjectDialogImpl.ASYNC_VALIDATION_DEBOUNCE_TIMER,
    );
    this.props.loadProjectCapabilitiesList();
  }

  debouncedProjectNameValidation = () => this.props.asyncValidate();

  handleSubmitData = (values): Promise<Project> => {
    const userList: User[] = values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_MEMBERS] || [];
    const userGroup: UserGroup = values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_USER_GROUP];

    const payload: ICreateProjectParams = {
      name: values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME],
      isPublic: this.props.isPublicProject,
      description: values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_DESCRIPTION] || null,
      userNames: userList.filter((user: User) => user.id !== PUBLIC_USER.id).map((user: User) => user.username),
      emailNotification: values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_SENDEMAILS] || false,
      userGroupId: userGroup ? userGroup.id : this.props.currentUser.userGroups[0].id,
      capabilityIds: values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_CAPABILITIES]
        ? values[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_CAPABILITIES].map(
            (projectCapability: Capability) => projectCapability.id,
          )
        : [],
    };

    return this.props.createProject(payload).then(
      (fulfilled) => {
        this.props.onApply();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render(): ReactElement {
    const userGroups: UserGroup[] = this.props.currentUser && this.props.currentUser.userGroups;

    return (
      <Dialog
        headerProps={{ title: `New ${getProjectLabel(true, false)}` }}
        mainActionButtonProps={{
          children: `Create ${getProjectLabel(true, false)}`,
          isDisabled: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        closeFn={() => {
          this.props.reset();
          this.props.onCancel();
        }}
        width={580}
      >
        <div>
          <InputReduxFormField
            tooltip={`Provide a short name for your new ${getProjectLabel(false, false)}`}
            componentClass="input"
            name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME}
            label={"Name"}
            placeholder={`Enter ${getProjectLabel(false, false)} name`}
            maxLength={DB_FIELD_MAX_LENGTH.PROJECT.NAME}
            component={InputField}
            handleChange={this.debouncedProjectNameValidation}
            validate={simpleFieldValidation}
          />

          <ReactSelectReduxFormField
            name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_USER_GROUP}
            label={
              (
                <HStack>
                  User group
                  <InfoToggletip
                    content={`This option will restrict the content that can be associated with this ${getProjectLabel(
                      false,
                      false,
                    )}.
It may also restrict the users who can be added to this ${getProjectLabel(false, false)}. More information about how to configure and
control access to content is available in the Documentation Library.`}
                  />
                </HStack>
              ) as any
            }
            placeholder="Select a user group..."
            component={ReactSelectField}
            options={this.props.currentUser && this.props.currentUser.userGroups}
            getOptionLabel={(userGroup: UserGroup) =>
              `${userGroup.name}${userGroup.description ? " (" + this.getUserGroupDescription(userGroup) + ")" : ""}`
            }
            //normalize={this.normalizeSelectedUserGroup} // NO! use the onChange
            handleChange={this.handleChangeOfSelectedUserGroup}
            isSearchable
            closeMenuOnSelect
            openMenuOnFocus
            isClearable={false}
            isMulti={false}
            hidden={userGroups.length < 2}
          />

          <ReactSelectReduxFormField
            name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_CAPABILITIES}
            label={"Settings"}
            placeholder={`Select ${getProjectLabel(false, false)} settings ... `}
            component={ReactSelectField}
            options={
              this.props.projectCapabilitiesListState && this.props.projectCapabilitiesListState.projectCapabilities
            }
            getOptionLabel={(option: Capability) => replaceProjectTermWithSystemSetting(option.name)}
            isLoading={this.props.projectCapabilitiesListState.isLoading}
            isSearchable
            closeMenuOnSelect
            openMenuOnFocus
            isClearable={true}
            isMulti={true}
          />

          <ReactSelectReduxFormField
            tooltip={`Share this ${getProjectLabel(false, false)} with other ${
              this.props.appName
            } users. These users will be able to view and edit ${getProjectLabel(false, false)} content.`}
            name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_MEMBERS}
            label={"Members"}
            className="users-selector"
            placeholder={`Invite other users to this ${getProjectLabel(false, false)}... `}
            options={this.buildUserList()}
            getOptionLabel={this.getUserLabel}
            component={ReactSelectField}
            normalize={this.normalizeSelectedListOfMembers}
            isMulti
            openMenuOnFocus
            closeMenuOnSelect={false}
            isClearable={false}
            components={{ IndicatorsContainer: () => null }}
            maxMenuHeight={200}
            forceWarnState={
              this.props.selectedUsers.filter((user) => user.id === PUBLIC_USER.id).length
                ? `With this setting all users of the selected user group will see this project and have access to the contents of this project`
                : null
            }
          />

          {this.props.selectedUsers.length > 0 && !this.props.isPublicProject && (
            <CheckboxReduxFormField
              name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_SENDEMAILS}
              label={`Send email to ${getProjectLabel(false, false)} members`}
              tooltipText={`Notify users by email that they have been invited to your new ${getProjectLabel(
                false,
                false,
              )}`}
              component={CheckboxField}
            />
          )}

          <InputReduxFormField
            tooltip={`Provide some optional text to describe this ${getProjectLabel(false, false)}`}
            componentClass="textarea"
            name={CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_DESCRIPTION}
            label="Description"
            placeholder="Optional description"
            maxLength={DB_FIELD_MAX_LENGTH.PROJECT.DESCRIPTION}
            component={InputField}
          />

          {this.props.error && <Callout intent={CalloutIntent.Danger}>{this.props.error}</Callout>}
        </div>
      </Dialog>
    );
  }

  getUserLabel = (u: User) => {
    const userGroupList = this.props.currentUser && this.props.currentUser.userGroups;
    if (u.id === PUBLIC_USER.id) {
      return `All (${userGroupList.length < 2 ? "visible to all users" : "visibility based on selected user group"})`;
    } else {
      return `${u.username}${u.firstName && u.lastName ? " (" + u.firstName + " " + u.lastName + ")" : ""}`;
    }
  };

  // we need to deal with the logic regarding the PUBLIC user
  normalizeSelectedListOfMembers = (members: User[]): User[] => {
    if (!members || members.length === 0) {
      return [];
    }
    // Cases:
    //   Added some user
    //     + Public     -> set only Public (removes all other users)
    //     + other user -> remove Public, add user
    if (members[members.length - 1].id === PUBLIC_USER.id) {
      return [PUBLIC_USER];
    }
    return members.filter((user: User) => user.id !== PUBLIC_USER.id);
  };

  // we need to adjust the set of selected members
  handleChangeOfSelectedUserGroup = (userGroup: UserGroup) => {
    const members: User[] = this.props.selectedUsers;

    if (members.length > 0 && !this.props.isPublicProject) {
      // meaning that there is a list of users defined
      const filteredProjectMembers: User[] = members.filter((member: User) =>
        userGroup.members.find((u: User) => u.username === member.username),
      );
      this.props.change(CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_MEMBERS, filteredProjectMembers);
    }
  };

  buildUserList = (): User[] => {
    // Rules:
    // You can either select 'All' or any number of the other users.
    //  If none is selected -> [ all , user1, user2, ... ]
    //  If all is selected -> [ ]
    //  If user1 is selected -> [ user2, ... ]

    const selectedUserGroup: UserGroup = this.props.selectedUserGroup
      ? this.props.selectedUserGroup
      : this.props.currentUser && this.props.currentUser.userGroups[0];

    const users: User[] = !selectedUserGroup
      ? []
      : selectedUserGroup.members.filter(
          (u: User) =>
            u.isActive && u.username !== PUBLIC_USER.username && u.username !== this.props.currentUser.username,
        );

    users.unshift(PUBLIC_USER);

    return users;
  };

  getUserGroupDescription(userGroup: UserGroup) {
    const desc = userGroup.description;
    if (desc.endsWith(".")) {
      return desc.substr(0, desc.length - 1);
    } else {
      return desc;
    }
  }

  static async asyncValidate(values, dispatch) {
    if (!values) {
      return;
    }

    const projectName: string = values?.[CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME]?.trim();

    if (await dispatch(validateProjectNameIsDefined(projectName, -1))) {
      throw {
        [CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME]: `${getProjectLabel(true, false)} name is already in use`,
      };
    }
  }
}

const CreateProjectDialog = connect<
  ICreateProjectDialogAppStateProps,
  ICreateProjectDialogDispatchProps,
  ICreateProjectDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: CreateProjectDialogImpl.PROJECT_DIALOG_FORM_NAME,
    // validate: CreateProjectDialogImpl.validate, // we use field level validation
    asyncValidate: CreateProjectDialogImpl.asyncValidate,
    asyncBlurFields: [CreateProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME], // so that only blurring this field triggers the async validation
    enableReinitialize: true, //this is required to pickup the initial values from own props when they change
  })(CreateProjectDialogImpl) as any,
);

export { CreateProjectDialog, CreateProjectDialogImpl };
export default CreateProjectDialog;
